import { useEffect, useState } from "react";

import { THUMBNAIL_STATUS, THUMBNAIL_TIMEOUT_MS } from "../misc/constants";

// Frames the picked contract in the confirmation dialog, so ops sees the
// actual document before it is written to the database.
//
// Framed from the local file rather than the URL the uploads service just
// returned: the bytes are the same, and a blob URL is same-origin, so the
// preview cannot be defeated by a storage bucket that serves the file without
// CORS headers. A preview that silently fails is worse than no preview in a
// confirm-before-save step — the point of the step is that something was
// actually looked at.
//
// `active` is the dialog's open state: the frame only exists while the dialog
// is mounted, so cancelling and picking again has to load it again.
const useContractThumbnail = ({ file, active }) => {
    const [status, setStatus] = useState(THUMBNAIL_STATUS.IDLE);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        if (!active || !file) {
            setStatus(THUMBNAIL_STATUS.IDLE);
            setPreviewUrl(null);
            return undefined;
        }

        const objectUrl = URL.createObjectURL(file);

        setPreviewUrl(objectUrl);
        setStatus(THUMBNAIL_STATUS.LOADING);

        return () => URL.revokeObjectURL(objectUrl);
    }, [file, active]);

    // Nothing inside the frame is reachable from here, so a frame that never
    // loads would leave the dialog spinning under a Save button. It is given a
    // deadline, after which the dialog falls back to the file details and the
    // "open the uploaded copy" link.
    useEffect(() => {
        if (status !== THUMBNAIL_STATUS.LOADING) return undefined;

        const timer = window.setTimeout(() => setStatus(THUMBNAIL_STATUS.ERROR), THUMBNAIL_TIMEOUT_MS);

        return () => window.clearTimeout(timer);
    }, [status, previewUrl]);

    return {
        previewUrl,
        status,
        // Guarded: a frame that loads after the deadline has already been
        // written off, and flipping it back to ready would contradict the
        // message on screen.
        onReady: () => setStatus((current) => (
            current === THUMBNAIL_STATUS.LOADING ? THUMBNAIL_STATUS.READY : current
        ))
    };
};

export default useContractThumbnail;
